import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClosingTask,
  ClosingTaskAction,
  ClosingTaskWorkStage,
  SmoUlClosingStageColumn,
  SmoUlClosingStatus,
  closeTasks,
  getClosingTasks,
} from '../services/api';
import {
  SortState,
  compareValues,
  formatDateShort,
  toggleSort,
} from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import { ClosingStatusIcon, closingStatusLabel } from '../components/ClosingStatusIcon';
import { formatDeadlineCompact, formatDeadlineRelativeTo } from '../utils/deadline';
import {
  TASK_STATUS_FILTER_OPTIONS,
  TaskLifecycleStatus,
  getTaskStatusLabel,
  normalizeTaskStatus,
} from '../utils/taskStatus';
import '../styles/DataPage.css';

type ClosingSortKey =
  | 'task_id'
  | 'short_name'
  | 'manager'
  | 'projectLead'
  | 'start_date'
  | 'end_date'
  | 'overdue'
  | 'task_status'
  | `accounting:${number}`
  | `stage:${number}`;

type ClosingFilterMenuKey =
  | 'task_id'
  | 'short_name'
  | 'manager'
  | 'project_lead'
  | 'overdue'
  | 'task_status'
  | `accounting:${number}`
  | `stage:${number}`
  | null;

const ACCOUNTING_STAGE_COLUMNS = 5;
const ACCOUNTING_STAGE_INDEXES = Array.from({ length: ACCOUNTING_STAGE_COLUMNS }, (_, index) => index);

const STAGE_STATUS_FILTER_OPTIONS: SmoUlClosingStatus[] = [
  'closed',
  'partial',
  'open',
  'na',
];
const OVERDUE_FILTER_OPTIONS = ['overdue', 'on_time', 'none'];

const STATUS_SORT_RANK: Record<SmoUlClosingStatus, number> = {
  na: 0,
  open: 1,
  partial: 2,
  waiting: 3,
  closed: 4,
};

function taskDetailPath(task: Pick<ClosingTask, 'id' | 'contracts_task_id'>): string {
  return `/tasks/${task.id}?contracts_task_id=${task.contracts_task_id}`;
}

function statusMatchesStageFilter(status: SmoUlClosingStatus, selected: string[]): boolean {
  if (selected.length === 0) return true;
  const normalized = status === 'waiting' ? 'open' : status;
  return selected.includes(normalized);
}

function overdueFilterLabel(value: string): string {
  if (value === 'overdue') return 'Просрочено';
  if (value === 'on_time') return 'Не просрочено';
  return 'Нет срока';
}

function parseFilterDay(value: string): number | null {
  const v = value.trim();
  if (!v) return null;
  const ms = new Date(`${v}T00:00:00`).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function dateInRange(value: string | null | undefined, from: string, to: string): boolean {
  const hasFrom = Boolean(from.trim());
  const hasTo = Boolean(to.trim());
  if (!hasFrom && !hasTo) return true;
  if (!value?.trim()) return false;

  const ts = parseFilterDay(value.slice(0, 10));
  if (ts === null) return true;

  const fromTs = hasFrom ? parseFilterDay(from) ?? Number.NEGATIVE_INFINITY : Number.NEGATIVE_INFINITY;
  const toTs = hasTo ? parseFilterDay(to) ?? Number.POSITIVE_INFINITY : Number.POSITIVE_INFINITY;
  return ts >= fromTs && ts <= toTs;
}

const ClosingTasksPage: React.FC = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [tasks, setTasks] = useState<ClosingTask[]>([]);
  const [executorStages, setExecutorStages] = useState<SmoUlClosingStageColumn[]>([]);
  const [rpStage, setRpStage] = useState<SmoUlClosingStageColumn | null>(null);
  const [companyStages, setCompanyStages] = useState<SmoUlClosingStageColumn[]>([]);
  const [actions, setActions] = useState<ClosingTaskAction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<string | null>(null);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<ClosingFilterMenuKey>(null);

  const [searchText, setSearchText] = useState('');
  const [filterTaskIds, setFilterTaskIds] = useState<string[]>([]);
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterManagerIds, setFilterManagerIds] = useState<string[]>([]);
  const [filterLeadIds, setFilterLeadIds] = useState<string[]>([]);
  const [filterStartFrom, setFilterStartFrom] = useState('');
  const [filterStartTo, setFilterStartTo] = useState('');
  const [filterEndFrom, setFilterEndFrom] = useState('');
  const [filterEndTo, setFilterEndTo] = useState('');
  const [filterOverdue, setFilterOverdue] = useState<string[]>([]);
  const [filterTaskStatuses, setFilterTaskStatuses] = useState<TaskLifecycleStatus[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<Record<number, string[]>>({});
  const [filterAccounting, setFilterAccounting] = useState<Record<number, string[]>>({});

  const [sortState, setSortState] = useState<SortState<ClosingSortKey>>({
    key: 'task_id',
    direction: 'desc',
  });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const canOpenPage = can('TASKS_CLOSING', 'VIEW');
  const canCreate = can('TASKS_CLOSING', 'CREATE');

  const statusColumns = useMemo(
    () => [...executorStages, ...(rpStage ? [rpStage] : []), ...companyStages],
    [executorStages, rpStage, companyStages],
  );

  const executorColCount = Math.max(executorStages.length, 1);
  const companyColCount = Math.max(companyStages.length, 1);

  const fetchData = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setIsLoading(false);
      setTasks([]);
      setExecutorStages([]);
      setRpStage(null);
      setCompanyStages([]);
      setActions([]);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const data = await getClosingTasks();
      setTasks(data.tasks);
      setExecutorStages(data.executor_stages ?? []);
      setRpStage(data.rp_stage ?? null);
      setCompanyStages(data.company_stages ?? []);
      setActions(data.actions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при загрузке данных');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [canOpenPage]);

  useEffect(() => {
    if (canOpenPage) {
      void fetchData();
    } else {
      setIsLoading(false);
      setTasks([]);
      setExecutorStages([]);
      setRpStage(null);
      setCompanyStages([]);
      setActions([]);
    }
  }, [canOpenPage, fetchData]);

  const clearFilters = (): void => {
    setSearchText('');
    setFilterTaskIds([]);
    setFilterNames([]);
    setFilterManagerIds([]);
    setFilterLeadIds([]);
    setFilterStartFrom('');
    setFilterStartTo('');
    setFilterEndFrom('');
    setFilterEndTo('');
    setFilterOverdue([]);
    setFilterTaskStatuses([]);
    setFilterStatuses({});
    setFilterAccounting({});
    setFilterMenuOpen(null);
  };

  const hasActiveFilters =
    filterTaskIds.length > 0 ||
    filterNames.length > 0 ||
    filterManagerIds.length > 0 ||
    filterLeadIds.length > 0 ||
    Boolean(filterStartFrom.trim()) ||
    Boolean(filterStartTo.trim()) ||
    Boolean(filterEndFrom.trim()) ||
    Boolean(filterEndTo.trim()) ||
    filterOverdue.length > 0 ||
    filterTaskStatuses.length > 0 ||
    Object.values(filterAccounting).some((values) => values.length > 0) ||
    Object.values(filterStatuses).some((values) => values.length > 0);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const taskIdFilterOptions = useMemo(() => {
    const set = new Set(tasks.map((task) => task.task_id));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru', { numeric: true }));
  }, [tasks]);

  const nameFilterOptions = useMemo(() => {
    const set = new Set(tasks.map((task) => task.short_name));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [tasks]);

  const managerFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const task of tasks) {
      if (task.manager_id != null) {
        set.add(String(task.manager_id));
      }
    }
    return [...set].sort((a, b) => {
      const left = tasks.find((task) => String(task.manager_id) === a)?.manager_name ?? a;
      const right = tasks.find((task) => String(task.manager_id) === b)?.manager_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [tasks]);

  const projectLeadFilterOptions = useMemo(() => {
    const set = new Set<string>();
    for (const task of tasks) {
      if (task.project_lead_id != null) {
        set.add(String(task.project_lead_id));
      }
    }
    return [...set].sort((a, b) => {
      const left = tasks.find((task) => String(task.project_lead_id) === a)?.project_lead_name ?? a;
      const right = tasks.find((task) => String(task.project_lead_id) === b)?.project_lead_name ?? b;
      return left.localeCompare(right, 'ru');
    });
  }, [tasks]);

  const getManagerLabel = useCallback(
    (id: string): string => tasks.find((task) => String(task.manager_id) === id)?.manager_name ?? id,
    [tasks],
  );

  const getLeadLabel = useCallback(
    (id: string): string => tasks.find((task) => String(task.project_lead_id) === id)?.project_lead_name ?? id,
    [tasks],
  );

  const getDeadline = useCallback((task: ClosingTask) => {
    return formatDeadlineRelativeTo(task.end_date, task.expense_closing_date);
  }, []);

  const getClosingCell = useCallback((task: ClosingTask, stageId: number) => {
    return (
      task.closing_statuses.find((item) => item.stage_id === stageId) ?? {
        stage_id: stageId,
        status: 'na' as SmoUlClosingStatus,
        closing_date: null,
      }
    );
  }, []);

  const getClosingStatus = useCallback(
    (task: ClosingTask, stageId: number): SmoUlClosingStatus => {
      return getClosingCell(task, stageId).status;
    },
    [getClosingCell],
  );

  const getAccountingStage = useCallback((task: ClosingTask, index: number): ClosingTaskWorkStage | null => {
    return task.work_stages?.[index] ?? null;
  }, []);

  const getAccountingStatus = useCallback(
    (task: ClosingTask, index: number): SmoUlClosingStatus => {
      const stage = getAccountingStage(task, index);
      if (!stage) return 'na';
      return stage.status === 'closed' ? 'closed' : 'open';
    },
    [getAccountingStage],
  );

  const overdueFilterValue = useCallback(
    (task: ClosingTask): string => {
      const deadline = getDeadline(task);
      if (!deadline.label) return 'none';
      return deadline.overdue ? 'overdue' : 'on_time';
    },
    [getDeadline],
  );

  const filteredTasks = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    return tasks.filter((task) => {
      const deadline = getDeadline(task);
      const searchable = [
        task.task_id,
        task.short_name,
        task.manager_name,
        task.project_lead_name,
        task.start_date ? formatDateShort(task.start_date) : '',
        task.end_date ? formatDateShort(task.end_date) : '',
        deadline.label ? deadline.label.toLowerCase() : '',
        formatDeadlineCompact(deadline),
        getTaskStatusLabel(normalizeTaskStatus(task.task_status)),
        ...ACCOUNTING_STAGE_INDEXES.map((index) => closingStatusLabel(getAccountingStatus(task, index))),
        ...statusColumns.map((stage) => closingStatusLabel(getClosingStatus(task, stage.stage_id))),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !text || searchable.includes(text);
      const matchesTaskId = filterTaskIds.length === 0 || filterTaskIds.includes(task.task_id);
      const matchesName = filterNames.length === 0 || filterNames.includes(task.short_name);
      const matchesManager =
        filterManagerIds.length === 0 || filterManagerIds.includes(String(task.manager_id || ''));
      const matchesLead =
        filterLeadIds.length === 0 || filterLeadIds.includes(String(task.project_lead_id || ''));
      const matchesStart = dateInRange(task.start_date, filterStartFrom, filterStartTo);
      const matchesEnd = dateInRange(task.end_date, filterEndFrom, filterEndTo);
      const matchesOverdue = filterOverdue.length === 0 || filterOverdue.includes(overdueFilterValue(task));
      const matchesTaskStatus =
        filterTaskStatuses.length === 0 ||
        filterTaskStatuses.includes(normalizeTaskStatus(task.task_status));
      const matchesAccounting = ACCOUNTING_STAGE_INDEXES.every((index) => {
        const selected = filterAccounting[index] ?? [];
        return statusMatchesStageFilter(getAccountingStatus(task, index), selected);
      });
      const matchesStatuses = statusColumns.every((stage) => {
        const selected = filterStatuses[stage.stage_id] ?? [];
        return statusMatchesStageFilter(getClosingStatus(task, stage.stage_id), selected);
      });

      return (
        matchesSearch &&
        matchesTaskId &&
        matchesName &&
        matchesManager &&
        matchesLead &&
        matchesStart &&
        matchesEnd &&
        matchesOverdue &&
        matchesTaskStatus &&
        matchesAccounting &&
        matchesStatuses
      );
    });
  }, [
    tasks,
    searchText,
    filterTaskIds,
    filterNames,
    filterManagerIds,
    filterLeadIds,
    filterStartFrom,
    filterStartTo,
    filterEndFrom,
    filterEndTo,
    filterOverdue,
    filterTaskStatuses,
    filterAccounting,
    filterStatuses,
    statusColumns,
    getDeadline,
    getAccountingStatus,
    getClosingStatus,
    overdueFilterValue,
  ]);

  const sortedTasks = useMemo(() => {
    const getSortValue = (task: ClosingTask, key: ClosingSortKey): string | number | undefined => {
      const deadline = getDeadline(task);
      if (key.startsWith('accounting:')) {
        const index = Number(key.slice(11));
        return STATUS_SORT_RANK[getAccountingStatus(task, index)];
      }
      if (key.startsWith('stage:')) {
        const stageId = Number(key.slice(6));
        return STATUS_SORT_RANK[getClosingStatus(task, stageId)];
      }
      const values: Record<
        Exclude<ClosingSortKey, `stage:${number}` | `accounting:${number}`>,
        string | number | undefined
      > = {
        task_id: task.task_id,
        short_name: task.short_name,
        manager: task.manager_name,
        projectLead: task.project_lead_name,
        start_date: task.start_date ?? undefined,
        end_date: task.end_date ?? undefined,
        overdue: task.end_date ?? undefined,
        task_status: getTaskStatusLabel(normalizeTaskStatus(task.task_status)),
      };
      if (key === 'overdue') {
        return deadline.overdue
          ? -(deadline.months ?? 0) * 30 - (deadline.days ?? 0)
          : (deadline.months ?? 0) * 30 + (deadline.days ?? 0);
      }
      return values[key as Exclude<ClosingSortKey, `stage:${number}` | `accounting:${number}`>];
    };

    const { key: sortKey, direction: sortDirection } = sortState;
    return [...filteredTasks].sort((left, right) =>
      compareValues(getSortValue(left, sortKey), getSortValue(right, sortKey), sortDirection),
    );
  }, [filteredTasks, sortState, getDeadline, getAccountingStatus, getClosingStatus]);

  const visibleIds = useMemo(() => sortedTasks.map((task) => task.id), [sortedTasks]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  const handleSort = (key: ClosingSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  const toggleSelectAllVisible = (): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleRowSelected = (id: number): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCloseAction = async (action: ClosingTaskAction): Promise<void> => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Выберите хотя бы одну задачу.');
      setSuccess(null);
      return;
    }
    setSaving(true);
    setError('');
    setSuccess(null);
    try {
      const created = await closeTasks({
        task_ids: ids,
        kind: action.kind,
        department_id: action.department_id ?? null,
      });
      setSuccess(`Создано записей о закрытии: ${created}`);
      setSelectedIds(new Set());
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка при закрытии задач');
    } finally {
      setSaving(false);
    }
  };

  const departmentActions = actions.filter((action) => action.kind === 'department');
  const expenseActions = actions.filter((action) => action.kind === 'expenses');
  const showActions = canCreate && actions.length > 0;
  const showSelection = showActions;
  const tableColSpan =
    (showSelection ? 1 : 0) + 8 + ACCOUNTING_STAGE_COLUMNS + 1 + executorColCount + 1 + 1 + 1 + companyColCount;

  const colgroup = (
    <colgroup>
      {showSelection ? <col className="closing-tasks-page__colgrid closing-tasks-page__colgrid--check" /> : null}
      <col className="contracts-page__colgrid in-progress-smo-ul-page__colgrid--task-id" />
      <col className="contracts-page__colgrid in-progress-smo-ul-page__colgrid--name" />
      <col className="contracts-page__colgrid contracts-page__colgrid--person" />
      <col className="contracts-page__colgrid contracts-page__colgrid--person" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid contracts-page__colgrid--date" />
      <col className="contracts-page__colgrid in-progress-contracts-page__colgrid--overdue" />
      <col className="contracts-page__colgrid contracts-page__colgrid--status" />
      {ACCOUNTING_STAGE_INDEXES.map((index) => (
        <col
          key={`accounting-${index}`}
          className="contracts-page__colgrid in-progress-smo-ul-page__colgrid--closing"
        />
      ))}
      <col className="closing-tasks-page__colgrid closing-tasks-page__colgrid--spacer" />
      {Array.from({ length: executorColCount }, (_, index) => (
        <col
          key={executorStages[index]?.stage_id ?? `executor-${index}`}
          className="contracts-page__colgrid in-progress-smo-ul-page__colgrid--closing"
        />
      ))}
      <col className="closing-tasks-page__colgrid closing-tasks-page__colgrid--spacer" />
      <col className="contracts-page__colgrid in-progress-smo-ul-page__colgrid--closing closing-tasks-page__colgrid--rp" />
      <col className="closing-tasks-page__colgrid closing-tasks-page__colgrid--spacer" />
      {Array.from({ length: companyColCount }, (_, index) => (
        <col
          key={companyStages[index]?.stage_id ?? `company-${index}`}
          className="contracts-page__colgrid in-progress-smo-ul-page__colgrid--closing"
        />
      ))}
    </colgroup>
  );

  const renderAccountingHeaders = (): React.ReactNode => {
    return (
      <th
        className="in-progress-smo-ul-page__th-closing closing-tasks-page__th-accounting closing-tasks-page__th-accounting-sub"
        colSpan={ACCOUNTING_STAGE_COLUMNS}
      >
        Зависимые этапы
      </th>
    );
  };

  const renderAccountingCells = (task: ClosingTask): React.ReactNode => {
    return ACCOUNTING_STAGE_INDEXES.map((index) => {
      const stage = getAccountingStage(task, index);
      if (!stage) {
        return (
          <td
            key={`accounting-${index}`}
            className="in-progress-smo-ul-page__td-closing closing-tasks-page__td-accounting"
          >
            —
          </td>
        );
      }
      return (
        <td
          key={stage.contract_stage_id}
          className="in-progress-smo-ul-page__td-closing closing-tasks-page__td-accounting"
          title={`${stage.stage_number}. ${stage.name}`}
        >
          <ClosingStatusIcon status={stage.status} closingDate={stage.closing_date} />
        </td>
      );
    });
  };

  const renderAccountingFooter = (): React.ReactNode => {
    return ACCOUNTING_STAGE_INDEXES.map((index) => (
      <td
        key={`accounting-f-${index}`}
        className="in-progress-smo-ul-page__td-closing closing-tasks-page__td-accounting"
      />
    ));
  };

  const renderStatusHeaders = (
    stages: SmoUlClosingStageColumn[],
    emptyLabel: string,
    extraClass = '',
  ): React.ReactNode => {
    if (stages.length === 0) {
      return <th className={`in-progress-smo-ul-page__th-closing ${extraClass}`.trim()}>{emptyLabel}</th>;
    }
    return stages.map((stage, index) => (
      <th
        key={stage.stage_id}
        className={`sortable in-progress-smo-ul-page__th-closing ${extraClass} ${index === 0 ? extraClass : ''}`.trim()}
        onClick={() => handleSort(`stage:${stage.stage_id}`)}
        role="button"
      >
        {stage.stage_name}
      </th>
    ));
  };

  const renderStatusCells = (task: ClosingTask, stages: SmoUlClosingStageColumn[], extraClass = ''): React.ReactNode => {
    if (stages.length === 0) {
      return <td className={`in-progress-smo-ul-page__td-closing ${extraClass}`.trim()}>—</td>;
    }
    return stages.map((stage) => {
      const cell = getClosingCell(task, stage.stage_id);
      return (
        <td key={stage.stage_id} className={`in-progress-smo-ul-page__td-closing ${extraClass}`.trim()}>
          <ClosingStatusIcon status={cell.status} closingDate={cell.closing_date} />
        </td>
      );
    });
  };

  const renderStatusFooter = (stages: SmoUlClosingStageColumn[], extraClass = ''): React.ReactNode => {
    const count = Math.max(stages.length, 1);
    return Array.from({ length: count }, (_, index) => (
      <td
        key={stages[index]?.stage_id ?? `empty-${index}`}
        className={`in-progress-smo-ul-page__td-closing ${extraClass}`.trim()}
      />
    ));
  };

  return (
    <div className="data-page contracts-page in-progress-contracts-page in-progress-smo-ul-page closing-tasks-page">
      {!canOpenPage ? (
        <div className="empty-state">Нет доступа к странице (требуется TASKS_CLOSING VIEW).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Это твои подлежащие закрытию задачи</h1>
            <div className="page-header-row__actions">
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}
          {success ? <div className="success-message">{success}</div> : null}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>
                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="ID задачи"
                      options={taskIdFilterOptions}
                      selected={filterTaskIds}
                      onSelectedChange={setFilterTaskIds}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'task_id'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'task_id' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Название задачи"
                      options={nameFilterOptions}
                      selected={filterNames}
                      onSelectedChange={setFilterNames}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'short_name'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'short_name' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Менеджер"
                      options={managerFilterOptions}
                      selected={filterManagerIds}
                      onSelectedChange={setFilterManagerIds}
                      formatOption={getManagerLabel}
                      isOpen={filterMenuOpen === 'manager'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'manager' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="РП"
                      options={projectLeadFilterOptions}
                      selected={filterLeadIds}
                      onSelectedChange={setFilterLeadIds}
                      formatOption={getLeadLabel}
                      isOpen={filterMenuOpen === 'project_lead'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'project_lead' : null)}
                    />
                  </div>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Начало</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterStartFrom}
                        onChange={(e) => setFilterStartFrom(e.target.value)}
                        title="Начало с"
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterStartTo}
                        onChange={(e) => setFilterStartTo(e.target.value)}
                        title="Начало по"
                      />
                    </div>
                  </label>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Срок</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterEndFrom}
                        onChange={(e) => setFilterEndFrom(e.target.value)}
                        title="Срок с"
                      />
                      <input
                        type="date"
                        className="filters-sum-block__input"
                        value={filterEndTo}
                        onChange={(e) => setFilterEndTo(e.target.value)}
                        title="Срок по"
                      />
                    </div>
                  </label>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Просрочка"
                      options={OVERDUE_FILTER_OPTIONS}
                      selected={filterOverdue}
                      onSelectedChange={setFilterOverdue}
                      formatOption={overdueFilterLabel}
                      isOpen={filterMenuOpen === 'overdue'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'overdue' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Статус"
                      options={TASK_STATUS_FILTER_OPTIONS}
                      selected={filterTaskStatuses}
                      onSelectedChange={(next) => setFilterTaskStatuses(next as TaskLifecycleStatus[])}
                      formatOption={(v) => getTaskStatusLabel(v)}
                      isOpen={filterMenuOpen === 'task_status'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'task_status' : null)}
                    />
                  </div>
                  {ACCOUNTING_STAGE_INDEXES.map((index) => (
                    <div key={`accounting-filter-${index}`} className="filters-field">
                      <SheetsColumnMultiFilter
                        label={`Этапы ${index + 1}`}
                        options={STAGE_STATUS_FILTER_OPTIONS}
                        selected={filterAccounting[index] ?? []}
                        onSelectedChange={(next) =>
                          setFilterAccounting((prev) => ({ ...prev, [index]: next }))
                        }
                        formatOption={(v) => closingStatusLabel(v as SmoUlClosingStatus)}
                        isOpen={filterMenuOpen === `accounting:${index}`}
                        onOpenChange={(open) =>
                          setFilterMenuOpen(open ? `accounting:${index}` : null)
                        }
                      />
                    </div>
                  ))}
                  {statusColumns.map((stage) => (
                    <div key={stage.stage_id} className="filters-field">
                      <SheetsColumnMultiFilter
                        label={stage.stage_name}
                        options={STAGE_STATUS_FILTER_OPTIONS}
                        selected={filterStatuses[stage.stage_id] ?? []}
                        onSelectedChange={(next) =>
                          setFilterStatuses((prev) => ({ ...prev, [stage.stage_id]: next }))
                        }
                        formatOption={(v) => closingStatusLabel(v as SmoUlClosingStatus)}
                        isOpen={filterMenuOpen === `stage:${stage.stage_id}`}
                        onOpenChange={(open) =>
                          setFilterMenuOpen(open ? `stage:${stage.stage_id}` : null)
                        }
                      />
                    </div>
                  ))}
                </div>
              </aside>
            )}

            <section className="table-content">
              {showActions ? (
                <div className="closing-tasks-page__toolbar">
                  {[...departmentActions, ...expenseActions].map((action) => (
                    <button
                      key={`${action.kind}-${action.department_id ?? action.stage_id}`}
                      type="button"
                      className={
                        action.kind === 'expenses'
                          ? 'closing-tasks-page__action-button closing-tasks-page__action-button--expenses'
                          : 'closing-tasks-page__action-button'
                      }
                      onClick={() => void handleCloseAction(action)}
                      disabled={saving || selectedIds.size === 0}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              ) : null}
              {isLoading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table contracts-page__table in-progress-contracts-page__table in-progress-smo-ul-page__table">
                      {colgroup}
                      <thead>
                        <tr>
                          {showSelection ? (
                            <th className="closing-tasks-page__th-check" rowSpan={2}>
                              <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                ref={(el) => {
                                  if (el) el.indeterminate = someVisibleSelected;
                                }}
                                onChange={toggleSelectAllVisible}
                                disabled={visibleIds.length === 0}
                                aria-label="Выбрать все"
                                title="Выбрать все"
                              />
                            </th>
                          ) : null}
                          <th className="sortable" rowSpan={2} onClick={() => handleSort('task_id')} role="button">
                            ID задачи
                          </th>
                          <th className="sortable" rowSpan={2} onClick={() => handleSort('short_name')} role="button">
                            Название задачи
                          </th>
                          <th
                            className="sortable contracts-page__th-person"
                            rowSpan={2}
                            onClick={() => handleSort('manager')}
                            role="button"
                          >
                            Менеджер
                          </th>
                          <th
                            className="sortable contracts-page__th-person"
                            rowSpan={2}
                            onClick={() => handleSort('projectLead')}
                            role="button"
                          >
                            РП
                          </th>
                          <th
                            className="sortable contracts-page__th-date"
                            rowSpan={2}
                            onClick={() => handleSort('start_date')}
                            role="button"
                          >
                            Начало
                          </th>
                          <th
                            className="sortable contracts-page__th-date"
                            rowSpan={2}
                            onClick={() => handleSort('end_date')}
                            role="button"
                          >
                            Срок
                          </th>
                          <th
                            className="sortable in-progress-contracts-page__th-overdue"
                            rowSpan={2}
                            onClick={() => handleSort('overdue')}
                            role="button"
                          >
                            Просрочка
                          </th>
                          <th
                            className="sortable contracts-page__th-status"
                            rowSpan={2}
                            onClick={() => handleSort('task_status')}
                            role="button"
                          >
                            Статус
                          </th>
                          <th
                            className="in-progress-smo-ul-page__th-closing-group closing-tasks-page__th-accounting-group"
                            colSpan={ACCOUNTING_STAGE_COLUMNS}
                          >
                            Бухгалтерия
                          </th>
                          <th className="closing-tasks-page__th-spacer" rowSpan={2} aria-hidden />
                          <th
                            className="in-progress-smo-ul-page__th-closing-group"
                            colSpan={executorColCount}
                          >
                            Исполнители
                          </th>
                          <th className="closing-tasks-page__th-spacer" rowSpan={2} aria-hidden />
                          <th
                            className="sortable in-progress-smo-ul-page__th-closing-group closing-tasks-page__th-rp"
                            rowSpan={2}
                            onClick={() => rpStage && handleSort(`stage:${rpStage.stage_id}`)}
                            role={rpStage ? 'button' : undefined}
                          >
                            РП
                          </th>
                          <th className="closing-tasks-page__th-spacer" rowSpan={2} aria-hidden />
                          <th
                            className="in-progress-smo-ul-page__th-closing-group closing-tasks-page__th-company-group"
                            colSpan={companyColCount}
                          >
                            Компания
                          </th>
                        </tr>
                        <tr>
                          {renderAccountingHeaders()}
                          {renderStatusHeaders(executorStages, '—')}
                          {renderStatusHeaders(companyStages, '—', 'closing-tasks-page__th-company')}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTasks.length === 0 ? (
                          <tr>
                            <td colSpan={tableColSpan} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedTasks.map((task) => {
                            const deadline = getDeadline(task);
                            const rpCell = rpStage
                              ? getClosingCell(task, rpStage.stage_id)
                              : { stage_id: 0, status: 'na' as SmoUlClosingStatus, closing_date: null };
                            return (
                              <tr
                                key={task.id}
                                className="clickable-row contracts-page__row--main"
                                onClick={(e) => {
                                  if (e.ctrlKey || e.metaKey) {
                                    return;
                                  }
                                  navigate(taskDetailPath(task));
                                }}
                              >
                                {showSelection ? (
                                  <td
                                    className="closing-tasks-page__td-check"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedIds.has(task.id)}
                                      onChange={() => toggleRowSelected(task.id)}
                                      aria-label={`Выбрать задачу ${task.task_id}`}
                                    />
                                  </td>
                                ) : null}
                                <td>
                                  <div className="contracts-page__clamp-2">
                                    <strong>{task.task_id}</strong>
                                  </div>
                                </td>
                                <td>
                                  <div className="contracts-page__clamp-2">{task.short_name || '—'}</div>
                                </td>
                                <td className="contracts-page__td-person">
                                  <div className="contracts-page__clamp-2">{task.manager_name}</div>
                                </td>
                                <td className="contracts-page__td-person">
                                  <div className="contracts-page__clamp-2">{task.project_lead_name}</div>
                                </td>
                                <td className="contracts-page__td-date">
                                  {task.start_date ? formatDateShort(task.start_date) : '—'}
                                </td>
                                <td className="contracts-page__td-date">
                                  {task.end_date ? formatDateShort(task.end_date) : '—'}
                                </td>
                                <td
                                  className={`in-progress-contracts-page__td-overdue${deadline.overdue ? ' in-progress-contracts-page__cell--overdue' : ''}`}
                                >
                                  {deadline.label ? (
                                    <div className="contracts-page__clamp-2 in-progress-contracts-page__deadline">
                                      <span className="in-progress-contracts-page__deadline-label">
                                        {deadline.label.toLowerCase()}
                                      </span>
                                      <span>{formatDeadlineCompact(deadline)}</span>
                                    </div>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="contracts-page__td-status">
                                  {getTaskStatusLabel(normalizeTaskStatus(task.task_status))}
                                </td>
                                {renderAccountingCells(task)}
                                <td className="closing-tasks-page__td-spacer" aria-hidden />
                                {renderStatusCells(task, executorStages)}
                                <td className="closing-tasks-page__td-spacer" aria-hidden />
                                <td className="in-progress-smo-ul-page__td-closing closing-tasks-page__td-rp">
                                  <ClosingStatusIcon
                                    status={rpCell.status}
                                    closingDate={rpCell.closing_date}
                                  />
                                </td>
                                <td className="closing-tasks-page__td-spacer" aria-hidden />
                                {renderStatusCells(task, companyStages, 'closing-tasks-page__td-company')}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer contracts-page__table in-progress-contracts-page__table in-progress-smo-ul-page__table">
                      {colgroup}
                      <tfoot>
                        <tr>
                          {showSelection ? <td className="closing-tasks-page__td-check" /> : null}
                          <td>Итого: {sortedTasks.length}</td>
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          <td />
                          {renderAccountingFooter()}
                          <td className="closing-tasks-page__td-spacer" />
                          {renderStatusFooter(executorStages)}
                          <td className="closing-tasks-page__td-spacer" />
                          <td className="in-progress-smo-ul-page__td-closing" />
                          <td className="closing-tasks-page__td-spacer" />
                          {renderStatusFooter(companyStages)}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default ClosingTasksPage;
